import React, { useEffect, useState } from 'react'
import moment from 'moment'
import ViewHeader from '../viewHeader/ViewHeader'
import FilterToolbar from '../filterToolbar/FilterToolbar'
import { useDispatch, useSelector } from 'react-redux'
import { selectFlags } from '../singleDeviation/store/selectors'
import { createSingleTableConfig } from '../tablesConfig/singleTableConfig'
import { setCaseAction } from '../../features/arende/store/caseReducer/actions'
import HandleFlagModal from '../../features/arende/components/handleFlag/HandleFlagModal'
import { LÄGE_FILTER } from '../tablesConfig/hanteringColumn'
import { setTableFilterAction } from '../tablesConfig/store/actions'
import { selectTableFilter } from '../tablesConfig/store/selectors'
import { useHistory, useLocation } from 'react-router-dom'

// Antd
import { Button, Table, Tabs, Tooltip } from 'antd'
import { DataType } from '../tablesConfig/filterconfig'
import '../../styles/flagLink.css'

export interface flagData {
  key: React.Key
  id: number
  flagga: number
  flaggnamn: string
  från: Date
  till: Date
  nivå: number
  created: Date
}

export interface IFlags {
  namn: string | undefined
  flaggor: DataType[]
}

interface singleObjInput {
  /** `id` finns för personal: två personer med samma namn ska inte dela sortering och filter (#99). */
  obj: { namn: string; id?: number }
  heading?: string
  /**
   * Hämtar om flaggorna efter en sparad hantering. Ligger hos föräldern eftersom det är
   * den som vet vilken typ och nyckel som ska hämtas — vyn här får bara listan via redux.
   * Utan den står Status-kolumnen kvar med gammalt läge tills man navigerar bort.
   */
  onHandled?: () => void
}

const SingleObject: React.FC<singleObjInput> = ({ obj, heading, onHandled }) => {
  const dispatch = useDispatch()
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<DataType[]>([])
  const [handleOpen, setHandleOpen] = useState<boolean>(false)
  const navigate = useHistory()

  // Redux states
  const flagsState = useSelector(selectFlags)
  // Sortering persisteras per individ (#41, samma mönster som #16) → överlever navigering.
  const filterState = useSelector(selectTableFilter)
  const sortKey = obj?.id !== undefined ? `individ::m${obj.id}` : `individ::${obj?.namn}`
  const setSorter = (sorter: any) => setTableFilterAction(sortKey, filterState?.[sortKey]?.filter ?? {}, sorter, dispatch)

  // Skapad-fönster: persisteras per individ i tableFilterReducer (sortKey.filter.from/to) så det
  // överlever navigering (#41). Reconciliation: PUSH (ny nav från lista) → ärv location.state (#28);
  // POP (tillbaka) → behåll sparat. Kall ingång → 12M-default.
  const location = useLocation()
  const inheritedDates = location.state as { from?: string; to?: string } | undefined
  const savedWindow = filterState?.[sortKey]?.filter as { from?: string; to?: string } | undefined
  const defaultFrom = moment().subtract(11, 'months').startOf('month').format('YYYY-MM-DD')
  const defaultTo = moment().endOf('month').format('YYYY-MM-DD')
  const [startDate, setStartDate] = useState(() => (navigate.action === 'POP' && savedWindow?.from ? savedWindow.from : inheritedDates?.from ?? defaultFrom))
  const [endDate, setEndDate] = useState(() => (navigate.action === 'POP' && savedWindow?.to ? savedWindow.to : inheritedDates?.to ?? defaultTo))
  const setWindow = (f: string, t: string) =>
    setTableFilterAction(sortKey, { ...(filterState?.[sortKey]?.filter ?? {}), from: f, to: t }, filterState?.[sortKey]?.sorter, dispatch)
  // PUSH (ny navigering) → seeda sparat fönster med arvet, så POP senare behåller rätt värde.
  useEffect(() => {
    if (navigate.action !== 'POP') setWindow(startDate, endDate)
  }, [])

  function handleDatesChange(range: any) {
    if (!range || !range[0] || !range[1]) return
    const f = range[0].startOf('month').format().split('T')[0]
    const t = range[1].endOf('month').format().split('T')[0]
    setStartDate(f)
    setEndDate(t)
    setWindow(f, t)
  }

  const filteredFlags = (flagsState || []).filter((f) => {
    const c = moment(f.created)
    return c.isSameOrAfter(moment(startDate), 'day') && c.isSameOrBefore(moment(endDate), 'day')
  })

  // Popover-filter persisteras per individ i tableFilterReducer (sortKey.filter) — samma objekt som
  // bär Skapad-fönstret (from/to, #41). Tidigare låg det i lokalt useState och nollades vid remount,
  // så filtret tappades när man gick in på en flaggbeskrivning och tillbaka (POP, #42).
  const savedFilter = (filterState?.[sortKey]?.filter ?? {}) as { deviation?: string[]; risk?: number[]; utförare?: string[]; hantering?: string[]; from?: string; to?: string }
  const filter = { deviation: savedFilter.deviation ?? [], risk: savedFilter.risk ?? [], utförare: savedFilter.utförare ?? [], hantering: savedFilter.hantering ?? [] }
  const setFilterPart = (key: 'deviation' | 'risk' | 'utförare' | 'hantering', values: any) =>
    setTableFilterAction(sortKey, { ...(filterState?.[sortKey]?.filter ?? {}), [key]: values }, filterState?.[sortKey]?.sorter, dispatch)
  // Rensa filter-dimensionerna men behåll Skapad-fönstret (from/to).
  const resetFilters = () =>
    setTableFilterAction(sortKey, { from: savedFilter.from, to: savedFilter.to }, filterState?.[sortKey]?.sorter, dispatch)

  const uniqStr = (vals: any[]) =>
    Array.from(new Set(vals.filter((v): v is string => !!v)))
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }))
  const fRows = filteredFlags as any[]
  const filterSelects = [
    { key: 'deviation', label: 'Flagga', options: uniqStr(fRows.map((r) => r.flaggnamn)), value: filter.deviation, onChange: (v: any) => setFilterPart('deviation', v) },
    {
      key: 'risk',
      label: 'Risknivå',
      options: Array.from(new Set(fRows.map((r) => r.nivå)))
        .sort((a, b) => (a as number) - (b as number))
        .map((v) => ({ value: v as number, label: v as number })),
      value: filter.risk,
      onChange: (v: any) => setFilterPart('risk', v),
    },
    { key: 'utförare', label: 'Utförare', options: uniqStr(fRows.map((r) => (r.utförare ?? []).map((u: any) => u.utförare_namn)).flat()), value: filter.utförare, onChange: (v: any) => setFilterPart('utförare', v) },
    // Fast alternativlista, som i listvyerna — härledd ur datan skulle "Pågående"
    // försvinna ur filtret precis när man vill kunna välja det.
    { key: 'hantering', label: 'Status', options: LÄGE_FILTER, value: filter.hantering, onChange: (v: any) => setFilterPart('hantering', v) },
  ]

  // Klient-sidefiltrering ovanpå Skapad-fönstret: OR inom en dimension, AND mellan.
  const displayedFlags = fRows.filter(
    (r) =>
      (!filter.deviation.length || filter.deviation.includes(r.flaggnamn)) &&
      (!filter.risk.length || filter.risk.includes(r.nivå)) &&
      (!filter.utförare.length || (r.utförare ?? []).some((u: any) => filter.utförare.includes(u.utförare_namn))) &&
      (!filter.hantering.length || filter.hantering.includes(r.hantering?.läge ?? 'ej_hanterad'))
  )

  const rowSelection = {
    fixed: 'right' as const,
    selectedRowKeys,
    onChange: (keys: React.Key[], rader: DataType[]) => {
      setSelectedRowKeys(keys)
      setSelectedRows(rader)
      setBtnDisabled(keys.length === 0)
      setCaseAction('SET_CASE', rader, null, dispatch)
    },
  }

  const efterHantering = () => {
    setHandleOpen(false)
    setSelectedRowKeys([])
    setSelectedRows([])
    setBtnDisabled(true)
    setCaseAction('SET_CASE', [], null, dispatch)
    onHandled?.()
  }

  const SELECTION_COLUMN_WIDTH = 32
  const singleColumns = createSingleTableConfig(displayedFlags, sortKey, { from: startDate, to: endDate, deviation: filter.deviation })

  // Dölj kolumner (lokalt state, default dölj Period — som listvyerna).
  const [deselectedColumns, setDeselectedColumns] = useState<string[]>(['Period'])
  const columnOptions = singleColumns
    .map((c) => c.title)
    .filter((t): t is string => typeof t === 'string')
    .map((t) => ({ value: t, label: t }))
  const visibleColumns = singleColumns.filter((c) => typeof c.title !== 'string' || !deselectedColumns.includes(c.title))
  const scrollX = visibleColumns.reduce((sum, col) => sum + ((col.width as number) || 0), 0) + SELECTION_COLUMN_WIDTH

  const action = (
    <Tooltip overlayClassName="flag-tooltip" title={btnDisabled ? <>Välj en eller flera rader<br />i tabellen nedan</> : ''}>
      <span style={btnDisabled ? { cursor: 'not-allowed' } : undefined}>
        <Button type="primary" onClick={() => setHandleOpen(true)} disabled={btnDisabled} style={{ width: 165, padding: '0 8px', ...(btnDisabled ? { pointerEvents: 'none' } : {}) }}>
          Hantera flagga{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
        </Button>
      </span>
    </Tooltip>
  )

  return (
    <>
      {obj && <ViewHeader heading={obj.namn} />}
      <Tabs style={{ marginTop: -12 }}>
        <Tabs.TabPane tab={'Flaggor'} key={'tab-avvikelser'}>
          <FilterToolbar
            startDate={startDate}
            endDate={endDate}
            onDatesChange={handleDatesChange}
            onOpenChange={() => undefined}
            filterSelects={filterSelects}
            onResetFilters={resetFilters}
            columnOptions={columnOptions}
            deselectedColumns={deselectedColumns}
            onDeselectedColumnsChange={setDeselectedColumns}
            total={`${displayedFlags.length} av ${filteredFlags.length}`}
            action={action}
          />
          <Table
            className="flag-table"
            columns={visibleColumns}
            rowSelection={{ ...rowSelection }}
            dataSource={displayedFlags}
            pagination={false}
            size="small"
            bordered
            scroll={{ x: scrollX, y: 'calc(100vh - 290px)' }}
            onChange={(_pagination, _filters, sorter) => setSorter(sorter)}
          />
        </Tabs.TabPane>
      </Tabs>
      <HandleFlagModal open={handleOpen} rows={selectedRows} onClose={() => setHandleOpen(false)} onDone={efterHantering} />
    </>
  )
}
export default SingleObject
